// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "@seahorse/ui";
import { createChart, CrosshairMode } from "lightweight-charts";
import {
  getAuthenticatedWebSocketUrl,
  getToken,
} from "../Utils/CoTraderUtils";
import {
  formatCompactDateTimeWithZone,
  formatLightweightChartAxisTime,
  formatLightweightChartTime,
  isCalendarChartInterval,
  parseUtcTimestampToMs,
} from "../Utils/timezone";
import { useDisplayTimeZone } from "../Utils/displayTimeZone";

const API_URL = process.env.REACT_APP_API_URL;
const PRICE_PRECISION = 4;
const Y_AXIS_PRICE_PRECISION = PRICE_PRECISION;
const Y_AXIS_PRICE_MIN_MOVE = 1 / 10 ** Y_AXIS_PRICE_PRECISION;
const CANDLE_SHOW_BAR_WIDTH_PX = 5.5;
const CANDLE_HIDE_BAR_WIDTH_PX = 4.5;

const getRawTickerFromCatalogValue = (symbol) => {
  const normalizedSymbol = String(symbol || "").trim();

  if (/^EQ-/i.test(normalizedSymbol)) {
    return normalizedSymbol.slice(normalizedSymbol.indexOf("-") + 1);
  }

  return normalizedSymbol;
};

function mapYahooToCandles(records) {
  if (!Array.isArray(records)) return [];
  const tsKeys = ["Datetime", "Date", "timestamp", "time", "datetime", "date"];

  const out = [];
  for (const row of records) {
    let rawTimestamp;
    for (const key of tsKeys) {
      if (row?.[key] != null) {
        rawTimestamp = row[key];
        break;
      }
    }
    if (rawTimestamp == null) continue;

    const timestampMs = parseUtcTimestampToMs(rawTimestamp);
    if (!Number.isFinite(timestampMs)) continue;
    const epochSec = Math.floor(timestampMs / 1000);

    const open = +row.Open;
    const high = +row.High;
    const low = +row.Low;
    const close = +row.Close;
    if (![epochSec, open, high, low, close].every(Number.isFinite)) continue;

    out.push({
      time: epochSec,
      open,
      high,
      low,
      close,
    });
  }

  out.sort((a, b) => a.time - b.time);

  const deduped = [];
  let lastTime = null;
  for (const row of out) {
    if (row.time !== lastTime) deduped.push(row);
    lastTime = row.time;
  }

  return deduped;
}

// Formatters read the zone through a ref so a zone change can relabel the
// existing chart via applyOptions instead of recreating it (which would drop
// the loaded series data).
function timeLabelFormatters(interval, timeZoneRef) {
  return {
    timeFormatter: (time) =>
      formatLightweightChartTime(time, interval, timeZoneRef.current),
    tickMarkFormatter: (time, tickMarkType) =>
      formatLightweightChartAxisTime(
        time,
        interval,
        tickMarkType,
        timeZoneRef.current
      ),
  };
}

function formatPrice(value, precision = PRICE_PRECISION) {
  return Number.isFinite(value) ? value.toFixed(precision) : "-";
}

function formatYAxisPrice(value) {
  return formatPrice(value, Y_AXIS_PRICE_PRECISION);
}

function bucketEpochSec(epochSec, interval) {
  const iv = String(interval || "1m");
  const mins = (n) => Math.floor(epochSec / (n * 60)) * (n * 60);

  if (iv === "1m") return mins(1);
  if (iv === "5m") return mins(5);
  if (iv === "15m") return mins(15);
  if (iv === "1h") return Math.floor(epochSec / 3600) * 3600;
  if (iv === "1d") return Math.floor(epochSec / 86400) * 86400;
  if (iv === "1wk") {
    return Math.floor(epochSec / (86400 * 7)) * (86400 * 7);
  }
  if (iv === "1mo") {
    const date = new Date(epochSec * 1000);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1) / 1000;
  }

  return mins(1);
}

function parseStreamCurrentPrice(rawPrice) {
  if (rawPrice == null) return null;

  if (typeof rawPrice === "object") {
    return rawPrice;
  }

  if (typeof rawPrice === "number") {
    return Number.isFinite(rawPrice)
      ? {
          current_price: rawPrice,
          spot_midpoint: rawPrice,
          spot_ask: rawPrice,
          spot_bid: rawPrice,
          timestamp: new Date().toISOString(),
        }
      : null;
  }

  if (typeof rawPrice !== "string") return null;

  try {
    const normalizedValue = rawPrice
      .replace(/'/g, '"')
      .replace(/Decimal\("([^"]+)"\)/g, "$1");
    const parsedValue = JSON.parse(normalizedValue);

    if (typeof parsedValue === "number") {
      return Number.isFinite(parsedValue)
        ? {
            current_price: parsedValue,
            spot_midpoint: parsedValue,
            spot_ask: parsedValue,
            spot_bid: parsedValue,
            timestamp: new Date().toISOString(),
          }
        : null;
    }

    if (typeof parsedValue === "object" && parsedValue !== null) {
      return parsedValue;
    }
  } catch {
    const fallbackValue = Number(rawPrice);
    return Number.isFinite(fallbackValue)
      ? {
          current_price: fallbackValue,
          spot_midpoint: fallbackValue,
          spot_ask: fallbackValue,
          spot_bid: fallbackValue,
          timestamp: new Date().toISOString(),
        }
      : null;
  }

  return null;
}

export default function CoTraderSpotTradingViewChart({
  symbol,
  streamSymbol,
  titleOverride,
  defaultPeriod = "5d",
  defaultInterval = "1m",
  dark,
  layoutVersion = 0,
  liveQuote = undefined,
  onPriceChange = undefined,
  customStartDate = undefined,
  customEndDate = undefined,
  enableLiveUpdates = true,
  hideIntervalControl = false,
  hidePeriodControl = false,
  compactControls = false,
  seriesDisplay = "line",
}) {
  const { timeZone: displayTimeZone } = useDisplayTimeZone();
  const displayTimeZoneRef = useRef(displayTimeZone);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({
    candle: null,
    closeLine: null,
  });
  const tooltipRef = useRef(null);
  const resizeObsRef = useRef(null);
  const lastAppliedExternalTickRef = useRef(null);
  const candlesRef = useRef([]);
  const chartDisplayModeRef = useRef(null);
  const liveWsRef = useRef(null);
  const theme = useTheme();

  const [period, setPeriod] = useState(defaultPeriod);
  const [interval, setIntervalValue] = useState(defaultInterval);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const isDark = useMemo(() => {
    if (typeof dark === "boolean") return dark;
    return true;
  }, [dark]);
  const displaySymbol = titleOverride || symbol;

  const ui = useMemo(() => {
    const colors = theme.globallink.colors;
    return {
      bg: colors.Grey[1200],
      panel: colors.Grey[1100],
      controlBg: colors.DarkGrey[600],
      border: colors.Slate[700],
      text: colors.White[500],
      muted: colors.Grey[500],
      grid: "rgba(148, 163, 184, 0.10)",
      tooltipBg: colors.DarkGrey[500],
      tooltipText: colors.Grey[500],
      overlay: "rgba(19, 24, 33, 0.42)",
      upCandle: "#168547",
      downCandle: "#D6111C",
      close: colors.Blue[300],
    };
  }, [theme]);

  const recomputeDerived = useCallback(() => {
    const candles = candlesRef.current || [];
    const { closeLine } = seriesRef.current;

    closeLine?.setData(
      candles.map((candle) => ({ time: candle.time, value: candle.close }))
    );
  }, []);

  const applyAdaptiveSeriesVisibility = useCallback((logicalRange = null) => {
    const chart = chartRef.current;
    const { candle, closeLine } = seriesRef.current;
    if (!chart || !candle || !closeLine) return;

    const visibleRange =
      logicalRange || chart.timeScale().getVisibleLogicalRange?.();
    const timeScaleWidth =
      chart.timeScale().width?.() || containerRef.current?.clientWidth || 0;

    if (!visibleRange || timeScaleWidth <= 0) {
      return;
    }

    const visibleBars = Math.max(1, visibleRange.to - visibleRange.from);
    const pixelsPerBar = timeScaleWidth / visibleBars;
    const currentMode = chartDisplayModeRef.current;
    const shouldShowCandles =
      seriesDisplay !== "line" &&
      (currentMode === "candles"
        ? pixelsPerBar >= CANDLE_HIDE_BAR_WIDTH_PX
        : pixelsPerBar >= CANDLE_SHOW_BAR_WIDTH_PX);
    const nextMode = shouldShowCandles ? "candles" : "line";

    if (containerRef.current) {
      containerRef.current.dataset.chartDisplayMode = nextMode;
      containerRef.current.dataset.chartPixelsPerBar = pixelsPerBar.toFixed(2);
    }

    if (currentMode === nextMode) {
      return;
    }

    chartDisplayModeRef.current = nextMode;
    candle.applyOptions({
      visible: shouldShowCandles,
      priceLineVisible: shouldShowCandles,
      lastValueVisible: shouldShowCandles,
    });
    closeLine.applyOptions({
      visible: !shouldShowCandles,
      priceLineVisible: !shouldShowCandles,
      lastValueVisible: !shouldShowCandles,
    });
  }, [seriesDisplay]);

  const applyLiveTickToCandles = useCallback(
    (price, tickEpochSec) => {
      const candles = candlesRef.current || [];
      if (
        !candles.length ||
        !Number.isFinite(price) ||
        !Number.isFinite(tickEpochSec)
      ) {
        return;
      }

      const timeBucket = bucketEpochSec(tickEpochSec, interval);
      const last = candles[candles.length - 1];

      if (last && last.time === timeBucket) {
        const next = {
          ...last,
          high: Math.max(last.high, price),
          low: Math.min(last.low, price),
          close: price,
        };

        candles[candles.length - 1] = next;
        candlesRef.current = candles;

        seriesRef.current.candle?.update(next);
        seriesRef.current.closeLine?.update({
          time: next.time,
          value: next.close,
        });
        onPriceChange?.(next.close);
        recomputeDerived();
        return;
      }

      const next = {
        time: timeBucket,
        open: price,
        high: price,
        low: price,
        close: price,
      };

      candles.push(next);
      candlesRef.current = candles;

      seriesRef.current.candle?.setData(candles);
      onPriceChange?.(next.close);
      recomputeDerived();
    },
    [interval, onPriceChange, recomputeDerived]
  );

  const fetchData = useCallback(async (nextSymbol, nextPeriod, nextInterval) => {
    setLoading(true);
    setErr(null);

    try {
      const quoteSymbol = getRawTickerFromCatalogValue(nextSymbol);
      const token = getToken();
      const query = new URLSearchParams({
        symbol: quoteSymbol,
        interval: nextInterval,
      });

      const parsedCustomStartDate =
        customStartDate != null ? new Date(customStartDate) : null;
      const parsedCustomEndDate =
        customEndDate != null ? new Date(customEndDate) : null;
      const hasCustomRange =
        parsedCustomStartDate != null &&
        parsedCustomEndDate != null &&
        !Number.isNaN(parsedCustomStartDate.getTime()) &&
        !Number.isNaN(parsedCustomEndDate.getTime());

      if (hasCustomRange) {
        query.set("startdatetime", parsedCustomStartDate.toISOString());
        query.set("enddatetime", parsedCustomEndDate.toISOString());
      } else {
        query.set("period", nextPeriod);
      }

      const url = `${process.env.REACT_APP_API_URL}/globallink/prices/get_prices_yf?${query.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json();
      if (!response.ok || !json?.success) {
        throw new Error(
          json?.errorObject?.message ||
            json?.detail ||
            json?.message ||
            json?.error ||
            "API error"
        );
      }
      if (json?.results?.error) throw new Error(json.results.error);

      const candles = mapYahooToCandles(json.results.data || []);

      return candles;
    } catch (error) {
      setErr(error.message || String(error));
      return [];
    } finally {
      setLoading(false);
    }
  }, [customEndDate, customStartDate]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    try {
      chartRef.current?.remove();
    } catch {}
    chartRef.current = null;

    const { timeFormatter, tickMarkFormatter } = timeLabelFormatters(
      interval,
      displayTimeZoneRef
    );
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: ui.bg },
        textColor: ui.text,
        attributionLogo: false,
      },
      localization: {
        priceFormatter: (price) => formatYAxisPrice(price),
        timeFormatter,
      },
      grid: {
        vertLines: { color: ui.grid },
        horzLines: { color: ui.grid },
      },
      timeScale: {
        borderColor: ui.border,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter,
      },
      rightPriceScale: {
        borderColor: ui.border,
      },
      crosshair: { mode: CrosshairMode.Normal },
    });

    const candle = chart.addCandlestickSeries({
      visible: false,
      upColor: ui.upCandle,
      downColor: ui.downCandle,
      borderUpColor: ui.upCandle,
      borderDownColor: ui.downCandle,
      wickUpColor: ui.upCandle,
      wickDownColor: ui.downCandle,
      priceLineVisible: true,
      lastValueVisible: true,
      priceFormat: {
        type: "custom",
        minMove: Y_AXIS_PRICE_MIN_MOVE,
        formatter: (price) => formatYAxisPrice(price),
      },
    });

    const closeLine = chart.addLineSeries({
      color: ui.close,
      lineWidth: 2,
      priceLineVisible: true,
      lastValueVisible: true,
      priceFormat: {
        type: "custom",
        minMove: Y_AXIS_PRICE_MIN_MOVE,
        formatter: (price) => formatYAxisPrice(price),
      },
    });

    chartRef.current = chart;
    seriesRef.current = { candle, closeLine };

    const tooltip = document.createElement("div");
    tooltip.style.position = "absolute";
    tooltip.style.left = "0";
    tooltip.style.top = "0";
    tooltip.style.zIndex = "1";
    tooltip.style.display = "none";
    tooltip.style.padding = "6px 8px";
    tooltip.style.borderRadius = "6px";
    tooltip.style.fontSize = "11px";
    tooltip.style.lineHeight = "1.35";
    tooltip.style.pointerEvents = "none";
    tooltip.style.border = `1px solid ${ui.border}`;
    tooltip.style.boxShadow = isDark
      ? "0 4px 14px rgba(0,0,0,0.42)"
      : "0 4px 14px rgba(0,0,0,0.12)";
    tooltip.style.background = ui.tooltipBg;
    tooltip.style.color = ui.tooltipText;
    tooltip.style.whiteSpace = "nowrap";
    tooltip.style.transform = "translate(0, 0)";

    tooltipRef.current = tooltip;
    containerRef.current.style.position = "relative";
    containerRef.current.appendChild(tooltip);

    chart.subscribeCrosshairMove((param) => {
      const point = param?.point;
      const containerWidth = containerRef.current?.clientWidth || 0;
      const containerHeight = containerRef.current?.clientHeight || 0;

      if (
        !param?.time ||
        !point ||
        point.x < 0 ||
        point.y < 0 ||
        point.x > containerWidth ||
        point.y > containerHeight
      ) {
        tooltip.style.display = "none";
        return;
      }

      const candleSeries = seriesRef.current.candle;
      const seriesValues = param.seriesData || param.seriesPrices;
      const seriesPrice = seriesValues?.get
        ? seriesValues.get(candleSeries)
        : seriesValues?.[candleSeries];
      const hoveredCandle =
        seriesPrice && typeof seriesPrice === "object" && "open" in seriesPrice
          ? seriesPrice
          : candlesRef.current.find((item) => item.time === +param.time);
      if (!hoveredCandle) {
        tooltip.style.display = "none";
        return;
      }
      const { close } = hoveredCandle;

      tooltip.innerHTML = `
        <div style="font-weight:700; color:${ui.text}">${displaySymbol}</div>
        <div style="opacity:0.78">${
          isCalendarChartInterval(interval)
            ? formatLightweightChartTime(
                param.time,
                interval,
                displayTimeZoneRef.current
              )
            : formatCompactDateTimeWithZone(
                +param.time * 1000,
                displayTimeZoneRef.current
              )
        }</div>
        <div style="font-weight:700; color:${ui.text}">${formatPrice(close)}</div>
      `;

      tooltip.style.display = "block";

      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      const margin = 10;
      let nextLeft = point.x + margin;
      let nextTop = point.y + margin;

      if (nextLeft + tooltipWidth > containerWidth - 8) {
        nextLeft = point.x - tooltipWidth - margin;
      }

      if (nextTop + tooltipHeight > containerHeight - 8) {
        nextTop = point.y - tooltipHeight - margin;
      }

      tooltip.style.left = `${Math.max(8, nextLeft)}px`;
      tooltip.style.top = `${Math.max(8, nextTop)}px`;
    });

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        chart.resize(width, height, true);
        requestAnimationFrame(() => applyAdaptiveSeriesVisibility());
      }
    });
    resizeObsRef.current = resizeObserver;
    resizeObserver.observe(containerRef.current);

    const handleVisibleLogicalRangeChange = (range) => {
      applyAdaptiveSeriesVisibility(range);
    };
    chart
      .timeScale()
      .subscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
    applyAdaptiveSeriesVisibility();

    return () => {
      try {
        chart
          .timeScale()
          .unsubscribeVisibleLogicalRangeChange(
            handleVisibleLogicalRangeChange
          );
      } catch {}
      if (tooltipRef.current?.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      }
      try {
        resizeObserver.disconnect();
      } catch {}
      try {
        chart.remove();
      } catch {}
      chartRef.current = null;
      chartDisplayModeRef.current = null;
    };
  }, [
    applyAdaptiveSeriesVisibility,
    displaySymbol,
    interval,
    isDark,
    ui,
  ]);

  useEffect(() => {
    displayTimeZoneRef.current = displayTimeZone;
    // Re-supplying the formatters makes lightweight-charts redraw axis and
    // crosshair labels in the newly selected zone.
    const { timeFormatter, tickMarkFormatter } = timeLabelFormatters(
      interval,
      displayTimeZoneRef
    );
    chartRef.current?.applyOptions({
      localization: { timeFormatter },
      timeScale: { tickMarkFormatter },
    });
  }, [displayTimeZone, interval]);

  useEffect(() => {
    let alive = true;

    (async () => {
      const candles = await fetchData(symbol, period, interval);
      if (!alive || !chartRef.current) return;

      const { candle, closeLine } =
        seriesRef.current;

      candle?.setData(candles);
      candlesRef.current = candles;
      if (candles.length > 0) {
        onPriceChange?.(candles[candles.length - 1].close);
      }

      closeLine?.setData(
        candles.map((item) => ({ time: item.time, value: item.close }))
      );

      const timeScale = chartRef.current.timeScale();
      timeScale.fitContent();
      const visibleRange = timeScale.getVisibleLogicalRange?.();
      if (visibleRange) {
        timeScale.setVisibleLogicalRange({
          from: visibleRange.from - 1,
          to: visibleRange.to + 1,
        });
      }
      applyAdaptiveSeriesVisibility();
    })();

    return () => {
      alive = false;
    };
  }, [
    fetchData,
    applyAdaptiveSeriesVisibility,
    interval,
    isDark,
    onPriceChange,
    period,
    symbol,
  ]);

  useEffect(() => {
    if (!enableLiveUpdates) {
      return undefined;
    }

    if (liveQuote !== undefined) {
      return undefined;
    }

    if (!symbol || !API_URL) return undefined;

    try {
      liveWsRef.current?.close();
    } catch {}
    liveWsRef.current = null;

    const normalizeSymbol = (value) =>
      getRawTickerFromCatalogValue(value)
        .trim()
        .replace(/=x$/i, "")
        .toLowerCase();

    const liveSymbol = getRawTickerFromCatalogValue(streamSymbol || symbol || "");
    const chartSymbol = normalizeSymbol(liveSymbol);
    const chatId = `PRICE:${liveSymbol},`;
    const userId = "chart_ticker_1";
    let isCancelled = false;
    let ws;

    const openSocket = async () => {
      try {
        const wsUrl = await getAuthenticatedWebSocketUrl(chatId, userId);
        if (isCancelled) return;

        ws = new WebSocket(wsUrl);
        liveWsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const raw = data.raw || {};
            const messageSymbol = normalizeSymbol(
              data.symbol || data.currency_pair || data.ticker
            );

            if (messageSymbol && messageSymbol !== chartSymbol) return;

            const parsedCurrentPrice =
              parseStreamCurrentPrice(data.current_price) ||
              parseStreamCurrentPrice(raw.current_price);
            const priceCandidates = [
              parsedCurrentPrice?.spot_midpoint,
              parsedCurrentPrice?.current_price,
              parsedCurrentPrice?.spot_ask,
              parsedCurrentPrice?.spot_bid,
              data.price,
              data.last,
              data.ltp,
              data.close,
              data.value,
              raw.price,
              raw.last,
              raw.ltp,
              raw.close,
            ];
            const price = Number(priceCandidates.find((item) => item != null));
            if (!Number.isFinite(price)) return;

            const rawTime =
              parsedCurrentPrice?.timestamp ??
              data.timestamp ??
              data.time ??
              data.ts ??
              raw.timestamp ??
              raw.time ??
              raw.ts ??
              null;

            const timestampMs = parseUtcTimestampToMs(rawTime);
            const epochSec = Number.isFinite(timestampMs)
              ? Math.floor(timestampMs / 1000)
              : Math.floor(Date.now() / 1000);

            applyLiveTickToCandles(price, epochSec);
          } catch (error) {
            console.error("[CoTraderSpotTradingViewChart] WS parse error:", error);
          }
        };

        ws.onerror = (error) =>
          console.error("[CoTraderSpotTradingViewChart] WS error:", error);
        ws.onclose = () => {};
      } catch (error) {
        if (!isCancelled) {
          console.error("[CoTraderSpotTradingViewChart] WS auth error:", error);
        }
      }
    };

    void openSocket();

    return () => {
      isCancelled = true;
      try {
        ws?.close();
      } catch {}
    };
  }, [applyLiveTickToCandles, enableLiveUpdates, liveQuote, streamSymbol, symbol]);

  useEffect(() => {
    if (!enableLiveUpdates) {
      return;
    }

    if (!liveQuote || typeof liveQuote !== "object") {
      return;
    }

    const rawPrice =
      liveQuote.current_price ??
      liveQuote.spot_midpoint ??
      liveQuote.price ??
      liveQuote.last;
    const nextPrice = Number(rawPrice);
    if (!Number.isFinite(nextPrice)) return;

    const rawTimestamp =
      liveQuote.timestamp ??
      liveQuote.time ??
      liveQuote.datetime ??
      liveQuote.date;

    const timestampMs = parseUtcTimestampToMs(rawTimestamp);
    let epochSec = Number.isFinite(timestampMs)
      ? Math.floor(timestampMs / 1000)
      : null;

    if (!Number.isFinite(epochSec)) {
      epochSec = Math.floor(Date.now() / 1000);
    }

    const dedupeKey = `${epochSec}:${nextPrice}`;
    if (lastAppliedExternalTickRef.current === dedupeKey) {
      return;
    }
    lastAppliedExternalTickRef.current = dedupeKey;

    applyLiveTickToCandles(nextPrice, epochSec);
  }, [applyLiveTickToCandles, enableLiveUpdates, liveQuote]);

  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;

    if (!chart || !container) {
      return undefined;
    }

    let frameId = null;
    const syncChartToContainer = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const { clientWidth, clientHeight } = container;
        if (clientWidth > 0 && clientHeight > 0) {
          chart.resize(clientWidth, clientHeight, true);
        }
      });
    };

    syncChartToContainer();

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [interval, layoutVersion, period, symbol]);

  const intervals = ["1m", "5m", "15m", "1h", "1d", "1wk", "1mo"];
  const periods = ["1d", "5d", "1mo", "3mo", "6mo", "1y", "5y", "max"];
  const showControls = !hideIntervalControl || !hidePeriodControl;

  return (
    <div
      className="h-100 w-100 d-flex flex-column"
      style={{
        background: ui.bg,
        minHeight: 0,
        position: "relative",
        zIndex: 0,
        isolation: "isolate",
      }}
    >
      {showControls ? (
        <div
          className="d-flex align-items-center border-bottom"
          style={{
            background: ui.panel,
            borderColor: ui.border,
            color: ui.text,
            padding: compactControls ? "6px 8px" : "8px",
          }}
        >
          <div
            className="ms-auto d-flex align-items-center"
            style={{ gap: compactControls ? "8px" : "12px" }}
          >
            {!hideIntervalControl ? (
              <div className="d-flex align-items-center">
                <span
                  className="me-1"
                  style={{
                    color: ui.muted,
                    fontSize: compactControls ? "11px" : undefined,
                  }}
                >
                  Interval
                </span>
                <select
                  aria-label="Chart interval"
                  value={interval}
                  onChange={(event) => setIntervalValue(event.target.value)}
                  className="form-select form-select-sm"
                  style={{
                    width: compactControls ? 76 : 110,
                    minHeight: compactControls ? 30 : undefined,
                    fontSize: compactControls ? "12px" : undefined,
                    background: ui.controlBg,
                    color: ui.text,
                    borderColor: ui.border,
                    boxShadow: "none",
                  }}
                >
                  {intervals.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {!hidePeriodControl ? (
              <div className="d-flex align-items-center">
                <span
                  className="me-1"
                  style={{
                    color: ui.muted,
                    fontSize: compactControls ? "11px" : undefined,
                  }}
                >
                  Period
                </span>
                <select
                  aria-label="Chart period"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                  className="form-select form-select-sm"
                  style={{
                    width: compactControls ? 76 : 110,
                    minHeight: compactControls ? 30 : undefined,
                    fontSize: compactControls ? "12px" : undefined,
                    background: ui.controlBg,
                    color: ui.text,
                    borderColor: ui.border,
                    boxShadow: "none",
                  }}
                >
                  {periods.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        ref={containerRef}
        style={{ flex: 1, minHeight: 0, position: "relative" }}
      >
        {loading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
            style={{
              background: ui.overlay,
              backdropFilter: "blur(2px)",
              zIndex: 1,
              color: ui.text,
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2 small" style={{ color: ui.muted }}>
              Updating chart...
            </div>
          </div>
        )}

        {err && (
          <div className="position-absolute top-50 start-50 translate-middle text-danger">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
